import { useSession } from '@context/sessionProvider';
import useSWR from 'swr';
import { ErrorProps, GridResponse } from '@/types';

type FetcherKey = [string] | [string, Record<string, string | number | undefined>];

export const createFetcher = (token: string) => async (queryParams: FetcherKey) => {
	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const rawParams = queryParams[1] ?? {};
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(rawParams)) {
		if (value !== undefined) {
			params[key] = String(value);
		}
	}

	const query = new URLSearchParams(params).toString();
	const fullUrl = query ? `${queryParams[0]}?${query}` : queryParams[0];
	const options: RequestInit = {
		method: 'GET',
		headers: headers,
	};

	const res = await fetch(fullUrl, options);

	if (!res.ok) {
		const { message, code } = await res.json();
		const error: ErrorProps = new Error(message || 'An error occurred while fetching the data.');
		error.status = res.status;
		error.code = code;
		throw error;
	}

	return res.json();
};

export function useConfigs() {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = (): FetcherKey => ['/api/config/get_all'];

	const { data, error, isLoading, mutate } = useSWR<GridResponse>(getKey, fetcherWithToken, { refreshInterval: 0 });

	return {
		configs: data?.data,
		error,
		isLoading,
		mutate,
	};
}
